<script setup>
import { useHead } from '@vueuse/head'
import { defineProps, computed } from 'vue'
import { useStore } from 'vuex'

import forEach from 'lodash/forEach'
import map from 'lodash/map'
import omit from 'lodash/omit'

import { buildURL } from '../../helpers/media'
import { Gallery } from '@web-nfb/vue-3-components'
import eyecon from '@assets/eyecon.svg'
import { cleanUpString } from '@helpers/sanitize'
import productionModelType from '@data/filter-production-model'

const store = useStore()

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  legendList: {
    type: Array,
    default: () => []
  },
  type: {
    type: String,
    default: undefined
  },
  shortCredits: {
    type: Object,
    default: () => {}
  },
  mediaList: {
    type: Array,
    default: () => []
  },
  mediaPattern: {
    type: String,
    default: 'https://dkyhanv6paotz.cloudfront.net/live/fit-in/{width}x{height}/medias/nfb_tube/{path}'
  },
  description: {
    type: String,
    default: ''
  },
  rating: {
    type: String,
    default: ''
  },
  warning: {
    type: String,
    default: ''
  },
  cc: {
    type: Boolean
  },
  dv: {
    type: Boolean
  },
  productionModel: {
    type: String,
    default: ''
  },
  agencyNote: {
    type: String,
    default: ''
  }
})

const hostname = store.getters['Global/hostname']

const mediaListForGallery = computed(() => {
  return map(props.mediaList, media => {
    return {
      path: media,
      width: 1600,
      title: props.title
    }
  })
})

useHead({
  meta: [
    {
      property: 'og:image',
      content: mediaListForGallery.value.length
        ? buildURL(props.mediaPattern, { ...mediaListForGallery.value[0] })
        : `${hostname}${eyecon}`
    },
    {
      property: 'og:title',
      content: props.title && cleanUpString(props.title)
    },
    {
      property: 'og:description',
      content: props.description && cleanUpString(props.description)
    },
    {
      name: 'twitter:card',
      content: props.description && cleanUpString(props.description)
    }
  ]
})

const locale = computed(() => store.getters['Global/locale'])

const legendListWithSeparator = computed(() => {
  if (!props.legendList) {
    return false
  }

  const items = [...props.legendList]
  const nbSeparators = items.length - 1
  for (let index = 0; index < nbSeparators; index++) {
    items.splice(index * 2 + 1, 0, { class: 'separator ' })
  }

  return items
})

const shortCreditsFiltered = computed(() => {
  const data = {}
  forEach(props.shortCredits, (items, label) => {
    if (items.length > 0) {
      data[label] = items
    }
  })

  return data
})

</script>
<template>
  <div
    class="detail-info"
    data-cy="info"
  >
    <div class="row">
      <div class="col-sm-12">
        <h1 v-html="title" />
        <p
          class="legend"
          v-if="legendList"
        >
          <span
            v-for="(item, i) in legendListWithSeparator"
            :key="i"
            v-bind="omit(item, ['html'])"
          >
            {{ item.html }}
          </span>
        </p>
        <div class="availability-rating-warning-container">
          <p
            class="accessibility"
            v-if="cc || dv"
          >
            <i
              :class="`icon-cc-${locale}-o`"
              v-if="cc"
              :title="$t('accessibility.accessibility.cc')"
            />
            <i
              class="icon-dv-o"
              v-if="dv"
              :title="$t('accessibility.accessibility.dv')"
            />
          </p>
          <p
            class="m-rating-warning"
            v-if="rating || warning"
          >
            <span
              v-if="rating"
              class="rating"
              v-html="rating"
              :title="$t(`accessibility.ratings.${rating}`)"
            />
            <!-- EO-4244 : Remove warnings. -->
            <!-- <span
              v-if="warning"
              class="warning"
            >
              <i class="icon-warning-o" />
              <span v-html="warning" />
            </span> -->
          </p>
        </div>
        <div
          v-if="shortCredits"
          class="short-credits"
        >
          <p
            v-for="(items, label) in shortCreditsFiltered"
            :key="label"
          >
            <span v-html="$t([`credits.${type}.${label}`, `credits.${label}`])" />:
            <span
              class="name"
              v-for="item in items"
              :key="item.slug"
            >
              <span
                v-html="`${item.first_name} ${item.last_name}`"
              />
              <span
                class="agency"
                v-if="item.agency"
                v-html="` (${item.agency})`"
              />
            </span>
          </p>
        </div>
      </div>
    </div>
    <div
      class="row"
      v-if="agencyNote"
    >
      <div class="col-sm-12">
        <p
          class="agency-note"
        >
          <span
            v-html="agencyNote"
          />
        </p>
      </div>
    </div>
    <div
      class="row"
      v-if="productionModel == productionModelType.ACQUISITION"
    >
      <div class="col-sm-12 description">
        <p
          class="production-model"
        >
          <span
            class="acquisition"
            v-html="$t('info.acquisition')"
          />
        </p>
      </div>
    </div>
    <div
      class="row"
      v-if="mediaList.length > 0"
    >
      <div class="col-sm-8 media-container">
        <Gallery
          :asset-list="mediaListForGallery"
          :path-pattern="mediaPattern"
        />
      </div>
    </div>
    <div
      class="row"
      v-if="description"
    >
      <div class="col-sm-12 description">
        <p v-html="description" />
      </div>
    </div>
  </div>
</template>

<style lang="less" scoped>
@import (reference) "~@less/shared/utilities.less";
@import (reference) "@{frontend_static_less}/public/layout/variables.less";
@import "@{frontend_static_less}/public/modules/m-rating-warning.less";

h1 {
  margin-top: 1rem;
  margin-bottom: 0;
}

.legend {
  .flex();
  .separator {
    height: 1em;
    display: block;
    width: 1px;
    background: @black;
    margin: 0 8px;
  }
}

.availability-rating-warning-container, .accessibility {
  display: flex;
  align-items: center;
}

.m-rating-warning {
  flex-wrap: nowrap;
}

.rating {
  color: @black;
}

.accessibility {
  i {
    margin-right: 1rem;

    &.icon-cc-en-o, &.icon-dv-o {
      font-size: 1.4rem;
    }
  }
}

.short-credits {
  p {
    margin: 0
  }

  .name {
    font-family: @font-serif-bold;
    &:not(:last-child) {
      &::after {
        content: ', '
      }
    }
  }
}

.media-container {
  margin: 0.75rem 0;
  max-width: 480px;
  img {
    max-width: 100%;
  }
}

.description {
  max-width: 850px;
}

.production-model {
  font-family: @font-serif-bold;
  margin-bottom: 0;
}

.agency-note {
  margin-top: 1.5rem;
}
</style>
